import { useEffect, useRef, useState } from 'react';
import { useDevices, useFetchLogs, useRefreshDevices } from '../api/hooks';
import { ApiError } from '../api/client';
import { Device } from '../api/types';
import { StateMessage } from '../components/StateMessage';
import { formatDateTime, timeAgo } from '../util/format';

/**
 * Devices page: lists every city and its registered devices, and lets the
 * operator request a log upload from any device.
 *
 * The list is loaded once on entry and served from cache thereafter; reading
 * fresh data from Firebase is an explicit action (the "Odśwież" button), to keep
 * Firestore reads to a minimum.
 */
export function DevicesPage() {
  const { data: cities, isLoading, isError, error, dataUpdatedAt } = useDevices();
  const refresh = useRefreshDevices();

  if (isLoading) {
    return <StateMessage title="Ładowanie urządzeń…" />;
  }
  if (isError) {
    return (
      <StateMessage tone="error" title="Nie udało się pobrać urządzeń">
        {(error as ApiError)?.message}
      </StateMessage>
    );
  }
  if (!cities || cities.length === 0) {
    return <StateMessage title="Brak miast w bazie" />;
  }

  const totalDevices = cities.reduce((n, c) => n + c.devices.length, 0);
  // `dataUpdatedAt` is set both by the initial load and by the refresh mutation
  // (which writes into this query's cache), so it reflects the true last read.
  const lastLoaded = dataUpdatedAt ? formatDateTime(new Date(dataUpdatedAt).toISOString()) : '—';

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>Urządzenia</h2>
          <span className="muted small">
            {cities.length} miast · {totalDevices} urządzeń · wczytano: {lastLoaded}
          </span>
        </div>
        <div className="refresh">
          <button
            className="btn"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            title="Ponownie wczytaj listę z bazy Firebase"
          >
            {refresh.isPending ? 'Odświeżanie…' : '↻ Odśwież z Firebase'}
          </button>
          {refresh.isError && (
            <span className="hint hint--err">{(refresh.error as ApiError)?.message}</span>
          )}
        </div>
      </div>

      {cities.map((city) => (
        <div key={city.id} className="card">
          <div className="card__head">
            <h3>
              {city.name} <span className="chip">{city.code}</span>
            </h3>
            <span className="muted">{city.devices.length} urządzeń</span>
          </div>

          {city.devices.length === 0 ? (
            <p className="muted card__empty">Brak zarejestrowanych urządzeń.</p>
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Urządzenie</th>
                    <th>Hash</th>
                    <th>Aktualizacja</th>
                    <th className="table__actions">Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {city.devices.map((device) => (
                    <DeviceRow key={device.deviceHash} device={device} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

/** Seconds the "Pobierz logi" button stays locked after a click, to prevent
 * an operator from firing many duplicate requests in quick succession. */
const FETCH_COOLDOWN_SECONDS = 10;

/** A single device row with an inline "fetch logs" control. */
function DeviceRow({ device }: { device: Device }) {
  const [filesCount, setFilesCount] = useState(1);
  const fetchLogs = useFetchLogs();
  // Synchronous guard against a rapid double-click: `isPending` is only updated
  // on the next render, so a second click in the same tick would otherwise fire
  // a second request before the button visually disables.
  const inFlight = useRef(false);
  // Cooldown (seconds remaining) enforcing a hard delay between requests.
  const [cooldown, setCooldown] = useState(0);

  // Tick the cooldown down once per second until it reaches zero.
  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const id = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const onFetch = () => {
    if (inFlight.current || cooldown > 0) {
      return;
    }
    inFlight.current = true;
    setCooldown(FETCH_COOLDOWN_SECONDS);
    fetchLogs.mutate(
      { deviceHash: device.deviceHash, filesCount },
      { onSettled: () => { inFlight.current = false; } },
    );
  };

  const locked = fetchLogs.isPending || cooldown > 0;
  const buttonLabel = fetchLogs.isPending
    ? 'Wysyłanie…'
    : cooldown > 0
      ? `Ponów za ${cooldown}s`
      : 'Pobierz logi';

  return (
    <tr className={device.stale ? 'row--stale' : undefined}>
      <td>
        <strong>{device.deviceName}</strong>
        {device.stale && <span className="chip chip--warn" title="Wpis starszy niż próg">nieaktywne?</span>}
      </td>
      <td className="mono">{device.deviceHash}</td>
      <td className="muted">{timeAgo(device.updatedAt)}</td>
      <td className="table__actions">
        <div className="fetch-control">
          <label className="fetch-control__count">
            Plików
            <input
              type="number"
              min={1}
              max={20}
              value={filesCount}
              disabled={locked}
              onChange={(e) =>
                setFilesCount(Math.min(20, Math.max(1, Number(e.target.value) || 1)))
              }
            />
          </label>
          <button className="btn btn--primary" onClick={onFetch} disabled={locked}>
            {buttonLabel}
          </button>
        </div>
        {cooldown > 0 && (
          <div
            className="cooldown-bar"
            title={`Kolejne pobranie możliwe za ${cooldown} s`}
            aria-hidden="true"
          >
            <span
              className="cooldown-bar__fill"
              style={{ width: `${(cooldown / FETCH_COOLDOWN_SECONDS) * 100}%` }}
            />
          </div>
        )}
        {fetchLogs.isSuccess && (
          <p className="hint hint--ok">Żądanie wysłane — sprawdź zakładkę „Żądania".</p>
        )}
        {fetchLogs.isError && (
          <p className="hint hint--err">{(fetchLogs.error as ApiError)?.message}</p>
        )}
      </td>
    </tr>
  );
}
